import { capitalize, isArray, isEmpty, pull, uniq } from 'lodash';
import { useContext, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionProvider,
  AccordionTitle,
  FormCheckbox,
  FormInput,
  FormSelect,
  Icon,
  ProductsContext,
  Skeleton
} from 'components';
import { FEATURE_FLAG_KEYS, useFeatureFlag, useSafeState } from 'hooks';

const LeisuresFilters = () => {
  const { t } = useTranslation();
  const { enabled: showSubsidyFilter } = useFeatureFlag(FEATURE_FLAG_KEYS.subsidyFilterEnabled);
  const { control, getValues, setValue } = useFormContext();
  const { leisureCategoryFilters, leisureCityFilters, loadingTravels, loadingLeisures, loadingLeisureCategory } =
    useContext(ProductsContext);

  // Status of the checkbox for each category (all | semi | '')
  const [allCategoriesCheckbox, setAllCategoriesCheckbox] = useSafeState({});
  // Index of category accordions that are opened by default
  const [categoryAccordionIndexes, setCategoryAccordionIndexes] = useSafeState([]);

  useEffect(() => {
    if (leisureCategoryFilters?.length > 0) {
      let accordionIndexes = [];
      const formLeisureCategory = getValues('leisureCategory');
      const allCategories = leisureCategoryFilters?.reduce((acc, category) => {
        let allCheckedStatus = '';
        if (isArray(formLeisureCategory[category.id])) {
          const allChecked = formLeisureCategory[category.id]?.length === category.subCategories?.length;
          allCheckedStatus = allChecked ? 'all' : 'semi';
        }
        // Values possibles: '' | 'semi' | 'all'
        // '' : no subcategories checked
        // 'semi' : some subcategories checked
        // 'all' : all subcategories checked
        acc[category.id] = allCheckedStatus;

        // Open accordion if some subcategories are checked
        if (['semi', 'all'].includes(allCheckedStatus)) {
          accordionIndexes.push(leisureCategoryFilters.indexOf(category));
        } else {
          accordionIndexes.push(0);
        }
        return acc;
      }, {});

      accordionIndexes = uniq(accordionIndexes);

      setAllCategoriesCheckbox(allCategories);
      setCategoryAccordionIndexes(accordionIndexes);
    }
  }, [leisureCategoryFilters, getValues('leisureCategory')]);

  const onSelectAllCategories = (category) => {
    const fieldName = `leisureCategory.${category.id}`;
    const subCategories = getValues(fieldName);

    // If some subCategories are checked, uncheck them all
    // If no subCategories are checked, check them all
    setValue(
      fieldName,
      subCategories?.length > 0 ? [] : category?.subCategories?.map((subCategory) => subCategory.id),
      {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true
      }
    );

    setAllCategoriesCheckbox((prev) => ({ ...prev, [category.id]: subCategories?.length > 0 ? '' : 'all' }));
  };

  const onSubCategoryChange = ({ checked, subCategory, category }) => {
    // Set subCategory value in form context
    const fieldName = `leisureCategory.${category.id}`;
    const subCategories = getValues(fieldName);
    let newValue = checked ? [...subCategories, subCategory] : pull(subCategories, subCategory);
    newValue = uniq(newValue);
    setValue(fieldName, newValue, { shouldDirty: true, shouldValidate: true, shouldTouch: true });

    const allSubCategoriesChecked = subCategories?.length === category.subCategories?.length;
    const someSubCategoriesChecked =
      subCategories?.length > 0 && subCategories?.length < category.subCategories?.length;

    // Change category checkbox state (all | semi | '') based on subCategories checked
    setAllCategoriesCheckbox((prev) => ({
      ...prev,
      [category.id]: allSubCategoriesChecked ? 'all' : someSubCategoriesChecked ? 'semi' : ''
    }));
  };

  return (
    <>
      <div className="p-6 pb-0 lg:border-b lg:border-gray-200">
        {showSubsidyFilter && (
          <FormCheckbox
            name="subsidizedOffers"
            labelI18nKey="form:subsidy"
            className="subsidy-checkbox mb-6 last:mb-0"
          />
        )}

        <div className="submit-container pb-6">
          <button type="submit" className="btn secondary w-full uppercase" disabled={loadingTravels || loadingLeisures}>
            {t('buttons.search')}
          </button>
        </div>

        <FormInput
          name="keywords"
          label={t('form:search')}
          icon={{ name: 'search', style: 'fad' }}
          placeholder={t('form:placeholders.searchLeisureResult')}
          className="search-input mb-6"
          dataTestId="resultLeisuresKeywords"
        />

        <Controller
          render={({ field: { onChange } }) => (
            <FormSelect
              options={leisureCityFilters || []}
              onChange={onChange}
              name="place"
              label={t('form:city')}
              placeholder={t('form:placeholders.select')}
              icon={{ name: 'location-dot', style: 'fad' }}
              className="mb-6"
              prefillFromParams
            />
          )}
          name="place"
          control={control}
        />
      </div>

      <div className="p-6">
        <fieldset className="leisure-categories">
          <legend className="text-sm font-semibold uppercase mb-6">{t('form:categoryLeisures')}</legend>

          {loadingLeisureCategory && (
            <>
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={`category-skeleton-${index}`}>
                  <div className="flex-center-between gap-2 mb-4">
                    <div className="flex items-center gap-2">
                      <Skeleton size="sm" />
                      <Skeleton classNames="w-32 h-8" />
                    </div>
                    <Skeleton size="sm" />
                  </div>

                  {Array.from({ length: 2 }).map((_, i) => (
                    <div className="flex-center-between gap-2 mb-4" key={`subcategory-skeleton-${i}`}>
                      <div className="flex items-center gap-2">
                        <Skeleton size="sm" />
                        <Skeleton classNames="w-32 h-8" />
                      </div>
                      <Skeleton size="sm" />
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}

          {!loadingLeisureCategory &&
            leisureCategoryFilters?.map((category, index) => (
              <AccordionProvider
                allowMultipleOpen
                defaultOpen={categoryAccordionIndexes}
                key={`leisure-category-${index}`}
              >
                <Accordion itemIndex={index}>
                  <AccordionTitle
                    icon={{ name: 'plus', class: 'text-yellow-500' }}
                    titlePrepend={
                      <FormCheckbox
                        name={`leisureCheckAll.${category.id}`}
                        onChange={() => onSelectAllCategories(category)}
                        customIcon={
                          <>
                            {!isEmpty(allCategoriesCheckbox?.[category.id]) && (
                              <Icon
                                name={allCategoriesCheckbox[category.id] === 'all' ? 'check' : 'minus'}
                                classes="text-primary-500"
                              />
                            )}
                          </>
                        }
                      />
                    }
                  >
                    {category.name}
                  </AccordionTitle>

                  <AccordionContent>
                    {category?.subCategories?.map((subCategory, i) => (
                      <FormCheckbox
                        key={`leisure-sub-category-${i}`}
                        name={`leisureCategory.${category.id}`}
                        customLabel={() => capitalize(subCategory.name)}
                        className="mb-6 ml-3 last:mb-6"
                        value={subCategory.id}
                        onChange={(checked, value) => onSubCategoryChange({ checked, subCategory: value, category })}
                      />
                    ))}
                  </AccordionContent>
                </Accordion>
              </AccordionProvider>
            ))}
        </fieldset>
      </div>
    </>
  );
};

export default LeisuresFilters;
